import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { hasPermission } from '../../rbac';
import { auditAPI } from '../../services/api';
import {
  AUDIT_MODULE_OPTIONS,
  MODULE_ENTITY_PRESETS,
  auditErrorMessage,
  auditExportFilename,
  buildAuditParams,
  emptyAuditFilters,
  extractFinancialDetails,
  formatAuditDateTime,
  groupEvents,
  humanizeAuditToken,
  prettyAuditJson,
  validateAuditFilters,
} from './auditLogUi';
import './AdminAuditLog.css';

const PAGE_SIZE = 25;

const saveExport = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const Snapshot = ({ title, value, tone }) => (
  <section className={`audit-snapshot ${tone || ''}`}>
    <header>
      <h3>{title}</h3>
      <span>{value == null ? 'Not recorded' : 'JSON snapshot'}</span>
    </header>
    <pre>{prettyAuditJson(value)}</pre>
  </section>
);

const EventDetail = ({ event, onClose }) => {
  if (!event) return null;
  const financial = extractFinancialDetails(event);
  const moduleDef = AUDIT_MODULE_OPTIONS.find(opt => opt.value === event.module);

  return (
    <div className="audit-modal-backdrop" onMouseDown={onClose}>
      <section
        className="audit-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="audit-detail-title"
        onMouseDown={mouseEvent => mouseEvent.stopPropagation()}
      >
        <header className="audit-modal-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="audit-eyebrow">Immutable audit event</span>
              {event.module && (
                <span className={`audit-module-badge ${event.module}`}>
                  {moduleDef?.icon} {moduleDef?.label || event.module}
                </span>
              )}
              {event.is_financial && (
                <span className="audit-financial-badge">💰 आर्थिक नोंद</span>
              )}
            </div>
            <h2 id="audit-detail-title">{humanizeAuditToken(event.action)}</h2>
            <p>{event.event_uid}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close audit event details">×</button>
        </header>

        <div className="audit-modal-body">
          <dl className="audit-facts">
            <div><dt>Occurred at</dt><dd>{formatAuditDateTime(event.occurred_at)}</dd></div>
            <div><dt>Entity</dt><dd>{humanizeAuditToken(event.entity_type)}</dd></div>
            <div><dt>Entity ID</dt><dd>{event.entity_id || 'Not recorded'}</dd></div>
            <div><dt>Actor</dt><dd>{event.actor?.full_name || 'System'}{event.actor?.id ? ` (#${event.actor.id})` : ''}</dd></div>
            <div><dt>Actor email</dt><dd>{event.actor?.email || 'Not recorded'}</dd></div>
            <div><dt>Request ID</dt><dd>{event.request_id || 'Not recorded'}</dd></div>
          </dl>

          {financial && (
            <div className="audit-financial-card">
              <h3>💰 आर्थिक विश्लेषण (Financial Oversight & Anti-Corruption Audit)</h3>
              <dl className="audit-financial-grid">
                <div>
                  <dt>रक्कम / ॲडजस्टमेंट</dt>
                  <dd style={{ color: '#166534' }}>{financial.amountFormatted || '—'}</dd>
                </div>
                <div>
                  <dt>बदलापूर्वीची मागणी</dt>
                  <dd>{financial.totalBeforeFormatted || '—'}</dd>
                </div>
                <div>
                  <dt>बदलानंतरची मागणी</dt>
                  <dd style={{ color: '#0369a1' }}>{financial.totalAfterFormatted || '—'}</dd>
                </div>
                <div>
                  <dt>निव्वळ फरक</dt>
                  <dd style={{ color: financial.differenceFormatted?.startsWith('-') ? '#15803d' : '#b91c1c' }}>
                    {financial.differenceFormatted || '—'}
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {event.reason && <section className="audit-reason"><h3>Recorded reason / कारण</h3><p>{event.reason}</p></section>}

          <div className="audit-snapshot-grid">
            <Snapshot title="Before (बदलापूर्वी)" value={event.before} tone="before" />
            <Snapshot title="After (बदलानंतर)" value={event.after} tone="after" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default function AdminAuditLog() {
  const { tenantSlug } = useParams();
  const { user } = useAuth();
  const canView = hasPermission(user, 'audit_log', 'view');
  const canExport = hasPermission(user, 'audit_log', 'export');
  const [draftFilters, setDraftFilters] = useState(emptyAuditFilters);
  const [filters, setFilters] = useState(emptyAuditFilters);
  const [events, setEvents] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, total_pages: 0 });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [filterError, setFilterError] = useState('');
  const [groupBy, setGroupBy] = useState('none');

  const page = pagination.page;
  const activeFilterCount = useMemo(
    () => Object.values(filters).filter(value => String(value || '').trim()).length,
    [filters]
  );

  const loadEvents = useCallback(async () => {
    if (!canView || !tenantSlug) return;
    setLoading(true);
    setError('');
    try {
      const response = await auditAPI.getEvents(
        tenantSlug,
        buildAuditParams(filters, { page, limit: PAGE_SIZE })
      );
      setEvents(response.data.events || []);
      setPagination(current => ({
        ...current,
        ...(response.data.pagination || {}),
        page: response.data.pagination?.page || page,
        limit: response.data.pagination?.limit || PAGE_SIZE,
      }));
    } catch (requestError) {
      setEvents([]);
      setError(auditErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [canView, filters, page, tenantSlug]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const selectModuleTab = moduleKey => {
    setDraftFilters(current => ({ ...current, module: moduleKey, entity_type: '' }));
    setFilters(current => ({ ...current, module: moduleKey, entity_type: '' }));
    setPagination(current => ({ ...current, page: 1 }));
    setFilterError('');
  };

  if (!canView) {
    return (
      <section className="audit-access-denied" role="alert">
        <span aria-hidden="true">🔒</span>
        <h1>Audit log unavailable</h1>
        <p>Your current Gram Panchayat membership does not include audit-log viewing permission.</p>
      </section>
    );
  }

  const updateFilter = event => {
    const { name, value } = event.target;
    setDraftFilters(current => ({ ...current, [name]: value }));
    setFilterError('');
  };

  const applyFilters = event => {
    event.preventDefault();
    const message = validateAuditFilters(draftFilters);
    if (message) {
      setFilterError(message);
      return;
    }
    setPagination(current => ({ ...current, page: 1 }));
    setFilters({ ...draftFilters });
  };

  const clearFilters = () => {
    const empty = emptyAuditFilters();
    setDraftFilters(empty);
    setFilters(empty);
    setPagination(current => ({ ...current, page: 1 }));
    setFilterError('');
  };

  const exportEvents = async () => {
    if (!canExport || exporting) return;
    setExporting(true);
    setError('');
    try {
      const response = await auditAPI.exportEvents(tenantSlug, buildAuditParams(filters));
      saveExport(response.data, auditExportFilename(new Date()));
    } catch (requestError) {
      setError(auditErrorMessage(requestError));
    } finally {
      setExporting(false);
    }
  };

  const firstVisible = pagination.total ? ((page - 1) * pagination.limit) + 1 : 0;
  const lastVisible = Math.min(page * pagination.limit, pagination.total || 0);

  const groupedEventList = groupEvents(events, groupBy);
  const currentEntityPresets = MODULE_ENTITY_PRESETS[draftFilters.module] || [];

  return (
    <div className="admin-audit-page">
      <header className="audit-page-header">
        <div>
          <span className="audit-eyebrow">Governance & accountability</span>
          <h1>ऑडिट लॉग आणि आर्थिक नियंत्रण (Audit Log & Financial Oversight)</h1>
          <p>
            ग्रामपंचायत मधील प्रत्येक आर्थिक व्यवहार, कर आकारणी, ॲडजस्टमेंट आणि प्रणालीतील बदलांवर बारकाईने लक्ष ठेवण्यासाठी सर्वंकष नोंद.
          </p>
        </div>
        {canExport && (
          <button className="audit-export-button" type="button" onClick={exportEvents} disabled={exporting}>
            <span aria-hidden="true">⇩</span> {exporting ? 'Preparing CSV…' : 'Export current results'}
          </button>
        )}
      </header>

      <section className="audit-summary" aria-label="Audit result summary">
        <div><span>एकूण नोंदी (Total Events)</span><strong>{pagination.total || 0}</strong></div>
        <div><span>सक्रिय फिल्टर्स (Active Filters)</span><strong>{activeFilterCount}</strong></div>
        <p><span aria-hidden="true">ℹ</span> आर्थिक व कर संबंधित नोंदी भ्रष्टाचार रोखण्यासाठी अपरिवर्तनीय (immutable) स्वरूपात सुरक्षित केल्या आहेत.</p>
      </section>

      {(error || filterError) && (
        <div className="audit-alert" role="alert">
          <span>{filterError || error}</span>
          {!filterError && <button type="button" onClick={loadEvents}>Try again</button>}
        </div>
      )}

      <section className="audit-panel">
        <nav className="audit-module-tabs" aria-label="विभागानुसार फिल्टर">
          {AUDIT_MODULE_OPTIONS.map(opt => (
            <button
              key={opt.value || 'all'}
              type="button"
              className={`audit-module-pill ${draftFilters.module === opt.value ? 'active' : ''}`}
              onClick={() => selectModuleTab(opt.value)}
            >
              <span>{opt.icon}</span>
              <span>{opt.label}</span>
            </button>
          ))}
        </nav>

        <form className="audit-filters" onSubmit={applyFilters} noValidate>
          <div className="audit-filter-heading">
            <div>
              <h2>तपशीलवार फिल्टर (Filter Events)</h2>
              <p>विशिष्ट मॉड्यूल, कृती किंवा करदात्याचा संदर्भ शोधण्यासाठी फिल्टर वापरा.</p>
            </div>
            <button type="button" onClick={clearFilters} disabled={!activeFilterCount && !Object.values(draftFilters).some(Boolean)}>
              Clear
            </button>
          </div>

          <div className="audit-filter-grid">
            <label>
              शोध (Search Keyword)
              <input
                name="q"
                value={draftFilters.q}
                onChange={updateFilter}
                placeholder="मागणी क्र., पावती क्र., कारण किंवा संदर्भ..."
                maxLength="100"
                autoComplete="off"
              />
            </label>

            {currentEntityPresets.length > 0 ? (
              <label>
                घटक प्रकार (Entity Preset)
                <select
                  name="entity_type"
                  value={draftFilters.entity_type}
                  onChange={updateFilter}
                  style={{ width: '100%', height: '39px', padding: '0 10px', borderRadius: '7px', border: '1px solid #cbd9e2', background: '#fff', fontSize: '0.75rem' }}
                >
                  {currentEntityPresets.map(preset => (
                    <option key={preset.value} value={preset.value}>{preset.label}</option>
                  ))}
                </select>
              </label>
            ) : (
              <label>
                घटक प्रकार (Entity type)
                <input
                  name="entity_type"
                  value={draftFilters.entity_type}
                  onChange={updateFilter}
                  placeholder="उदा. demand_adjustment, property"
                  maxLength="80"
                  autoComplete="off"
                />
              </label>
            )}

            <label>
              घटक ओळख (Entity ID / UID)
              <input
                name="entity_id"
                value={draftFilters.entity_id}
                onChange={updateFilter}
                placeholder="उदा. demand_uid किंवा रेकॉर्ड आयडी"
                maxLength="100"
                autoComplete="off"
              />
            </label>

            <label>
              कृती (Action Identifier)
              <input
                name="action"
                value={draftFilters.action}
                onChange={updateFilter}
                placeholder="उदा. demand_adjustment.approved"
                maxLength="100"
                autoComplete="off"
              />
            </label>

            <label>
              अधिकारी युजर आयडी (Actor User ID)
              <input
                name="actor_user_id"
                type="number"
                min="1"
                step="1"
                value={draftFilters.actor_user_id}
                onChange={updateFilter}
                placeholder="उदा. 4"
              />
            </label>

            <label>
              दिनांक पासून (From Date)
              <input name="from" type="date" value={draftFilters.from} onChange={updateFilter} />
            </label>

            <label>
              दिनांक पर्यंत (To Date)
              <input name="to" type="date" value={draftFilters.to} onChange={updateFilter} />
            </label>
          </div>

          <footer>
            <span>{activeFilterCount ? `${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'} applied` : 'Showing latest events'}</span>
            <button className="audit-apply-button" type="submit">Apply filters</button>
          </footer>
        </form>

        <div className="audit-table-heading">
          <div>
            <h2>नोंदींचा इतिहास (Event History)</h2>
            <p>नवीनतम नोंदी सर्वप्रथम दाखवल्या जातात.</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', color: '#475569', fontWeight: 700 }}>
              वर्गीकरण (Group by):
              <select
                value={groupBy}
                onChange={e => setGroupBy(e.target.value)}
                style={{ padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd9e2', fontSize: '0.72rem', background: '#fff' }}
              >
                <option value="none">सलग यादी (Chronological)</option>
                <option value="module">विभागानुसार गट (By Module)</option>
                <option value="date">दिनांकानुसार गट (By Date)</option>
                <option value="actor">अधिकार्यानुसार गट (By User)</option>
              </select>
            </label>
            <button type="button" onClick={loadEvents} disabled={loading} aria-label="Refresh audit events">
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="audit-loading" role="status"><span /><p>Loading audit events…</p></div>
        ) : events.length === 0 ? (
          <div className="audit-empty"><span aria-hidden="true">⌕</span><h3>कोणतीही नोंद आढळली नाही</h3><p>फिल्टर्स तपासा किंवा वेगळे पर्याय निवडून पुन्हा प्रयत्न करा.</p></div>
        ) : (
          <div className="audit-table-wrap">
            <table className="audit-table">
              <thead>
                <tr>
                  <th>विभाग (Module)</th>
                  <th>दिनांक व वेळ</th>
                  <th>कृती (Action)</th>
                  <th>घटक (Entity)</th>
                  <th>अधिकारी (Actor)</th>
                  <th>कारण व आर्थिक तपशील</th>
                  <th><span className="sr-only">Details</span></th>
                </tr>
              </thead>
              <tbody>
                {groupedEventList.map(group => (
                  <React.Fragment key={group.key}>
                    {groupBy !== 'none' && (
                      <tr>
                        <td colSpan={7} className="audit-group-header">
                          <span>{group.icon} {group.label}</span>
                          <span>({group.events.length} नोंदी)</span>
                        </td>
                      </tr>
                    )}
                    {group.events.map(event => {
                      const fin = extractFinancialDetails(event);
                      const mod = AUDIT_MODULE_OPTIONS.find(o => o.value === event.module);
                      return (
                        <tr key={event.event_uid}>
                          <td>
                            <span className={`audit-module-badge ${event.module || 'general'}`}>
                              {mod?.icon || '📌'} {mod?.label ? mod.label.split(' ')[0] : (event.module || 'प्रणाली')}
                            </span>
                            {event.is_financial && (
                              <div style={{ marginTop: 2 }}>
                                <span className="audit-financial-badge">💰 आर्थिक</span>
                              </div>
                            )}
                          </td>
                          <td>
                            <strong>{formatAuditDateTime(event.occurred_at)}</strong>
                            <small>{event.request_id ? `Req: ${event.request_id}` : event.event_uid}</small>
                          </td>
                          <td>
                            <span className="audit-action-chip">{humanizeAuditToken(event.action)}</span>
                            <small>{event.action}</small>
                          </td>
                          <td>
                            <strong>{humanizeAuditToken(event.entity_type)}</strong>
                            <small>{event.entity_id || 'No entity ID'}</small>
                          </td>
                          <td>
                            <strong>{event.actor?.full_name || 'System'}</strong>
                            <small>{event.actor?.id ? `User #${event.actor.id}` : 'Automated'}</small>
                          </td>
                          <td className="audit-reason-cell">
                            <div>{event.reason || <span>Not recorded</span>}</div>
                            {fin?.amountFormatted && (
                              <span className="audit-amount-chip">
                                रक्कम: {fin.amountFormatted}
                              </span>
                            )}
                            {fin?.differenceFormatted && (
                              <span className="audit-amount-chip" style={{ marginLeft: 4 }}>
                                बदल: {fin.differenceFormatted}
                              </span>
                            )}
                          </td>
                          <td>
                            <button className="audit-detail-button" type="button" onClick={() => setSelectedEvent(event)}>
                              View details
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <footer className="audit-pagination">
          <span>Showing {firstVisible}-{lastVisible} of {pagination.total || 0}</span>
          <div>
            <button type="button" onClick={() => setPagination(current => ({ ...current, page: current.page - 1 }))} disabled={loading || page <= 1}>← Previous</button>
            <span>Page {page} of {Math.max(1, pagination.total_pages || 1)}</span>
            <button type="button" onClick={() => setPagination(current => ({ ...current, page: current.page + 1 }))} disabled={loading || page >= (pagination.total_pages || 1)}>Next →</button>
          </div>
        </footer>
      </section>

      <EventDetail event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </div>
  );
}
